import { BorderRadius } from '@persona-id/design-tokens';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { ToggleGroupMultipleProps, ToggleGroupSingleProps } from '@radix-ui/react-toggle-group';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import IconButton from 'design-system/components/Button/IconButton';
import useFormField from 'design-system/components/FormControl/useFormField';

export interface ToggleGroupProps {
  id?: string;
  className?: string;
  fullWidth?: boolean;
}

const OptionItem = styled(IconButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex: 1;
  border-radius: ${BorderRadius.Md};
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.color.variants.basic.default};
  white-space: nowrap;

  // radix-toggle-group adds a wrapping div to the options, so this is to
  // make sure the content is always center-aligned
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &[data-state='on'] {
    background-color: ${({ theme }) => theme.color.variants.basic.active};
  }

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:not(:first-child):not(:last-child) {
    border-radius: 0;
  }

  &[tabindex='0'] {
    z-index: 1;
  }
`;

const ToggleGroupContainer = styled.div<{ fullWidth?: boolean }>`
  display: inline-flex;
  justify-content: space-between;
  border-radius: 4px;
  width: ${({ fullWidth }) => (fullWidth ? `100%` : `max-content`)};
  gap: 1px;

  > * {
    ${({ fullWidth }) =>
      fullWidth &&
      css`
        flex: 1;
      `}
  }
`;

const ToggleGroup = ({
  children,
  fullWidth,
  ...props
}: (ToggleGroupSingleProps | ToggleGroupMultipleProps) & ToggleGroupProps) => {
  const { disabled } = props;
  const field = useFormField({ inputId: props.id, disabled });

  return (
    <ToggleGroupPrimitive.Root
      {...props}
      disabled={field.disabled}
      aria-describedby={field.helperTextId}
      aria-errormessage={field.hasError ? field.helperTextId : undefined}
      aria-invalid={field.hasError}
    >
      <ToggleGroupContainer fullWidth={fullWidth} {...props}>
        {children}
      </ToggleGroupContainer>
    </ToggleGroupPrimitive.Root>
  );
};

export default ToggleGroup;

const ToggleItem = forwardRef<HTMLButtonElement, ToggleGroupPrimitive.ToggleGroupItemProps>(
  function ToggleItem(props: ToggleGroupPrimitive.ToggleGroupItemProps, forwardedRef) {
    return <OptionItem ref={forwardedRef} {...props} />;
  },
);

export const ToggleGroupItem = forwardRef<
  HTMLButtonElement,
  ToggleGroupPrimitive.ToggleGroupItemProps
>(function ToggleGroupItem({ children, ...props }, forwardedRef) {
  return (
    <ToggleGroupPrimitive.Item {...props} ref={forwardedRef} asChild>
      <ToggleItem {...props}>{children}</ToggleItem>
    </ToggleGroupPrimitive.Item>
  );
});
